import { Component, signal, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { NbAdjustment, NbPopoverDirective, NbPosition, NbToastrService } from '@nebular/theme';
import {
  EquipmentDetailDto,
  EquipmentMergeDto,
  EquipmentProtocolTypeDetailDto,
  EquipmentService,
  ProtocolTypeItemDto,
  ProtocolTypeService,
} from '@artemis-software/wr-api';
import { PendingChanges } from '@guards/pending-changes.guard';

@Component({
  standalone: false,
  selector: 'wr-tga-type-protocol-types',
  templateUrl: './tga-type-protocol-types.component.html',
  styleUrls: ['./tga-type-protocol-types.component.scss'],
})
export class TgaTypeProtocolTypesComponent implements PendingChanges {

  @ViewChild('protocolTypePicker') protocolTypePicker?: NbPopoverDirective;

  readonly NbPosition = NbPosition;
  readonly NbAdjustment = NbAdjustment;

  readonly saving$ = new BehaviorSubject(false);
  readonly isEditing = signal(false);
  readonly canEdit = signal(false);
  readonly rows = signal<EquipmentProtocolTypeDetailDto[]>([]);
  readonly allProtocolTypes$ = new BehaviorSubject<ProtocolTypeItemDto[]>([]);

  tgaTypeId: string | null = null;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly equipmentService: EquipmentService,
    private readonly protocolTypeService: ProtocolTypeService,
    private readonly toastrService: NbToastrService,
  ) {
    this.route.paramMap.subscribe(params => {
      const id = this.tgaTypeId = params.get('id');
      if (id && id !== 'new') {
        this.loadData(id);
      }
    });
  }

  loadData(id: string): void {
    this.equipmentService.findById(id).subscribe((dto: EquipmentDetailDto) => {
      this.canEdit.set(dto.permissions.canEdit);
      this.rows.set(dto.protocolTypes ?? []);
    });
  }

  loadAllProtocolTypes(): void {
    this.protocolTypeService.findAll({}).subscribe(types => {
      const selectedIds = new Set(this.rows().map(r => r.protocolType.id));
      this.allProtocolTypes$.next(types.filter(t => !selectedIds.has(t.id)));
    });
  }

  selectProtocolType(type: ProtocolTypeItemDto): void {
    const newEntry: EquipmentProtocolTypeDetailDto = {
      protocolType: type,
      allowForMaintenanceTimer: false,
    };
    this.rows.update(rows => [...rows, newEntry]);
    this.protocolTypePicker?.hide();
    this.loadAllProtocolTypes();
  }

  toggleMaintenanceTimer(index: number, checked: boolean): void {
    this.rows.update(rows =>
      rows.map((r, i) => i === index ? { ...r, allowForMaintenanceTimer: checked } : r)
    );
  }

  removeRow(index: number): void {
    this.rows.update(rows => rows.filter((_, i) => i !== index));
    this.loadAllProtocolTypes();
  }

  onEdit(): void {
    this.isEditing.set(true);
    this.loadAllProtocolTypes();
  }

  async cancel(): Promise<void> {
    this.isEditing.set(false);
    if (this.tgaTypeId) {
      this.loadData(this.tgaTypeId);
    }
  }

  async submit(): Promise<void> {
    if (!this.tgaTypeId) return;
    try {
      this.saving$.next(true);
      const mergeDto: EquipmentMergeDto = {
        id: this.tgaTypeId,
        protocolTypeConfigs: this.rows().map(r => ({
          protocolTypeId: r.protocolType.id,
          allowForMaintenanceTimer: r.allowForMaintenanceTimer,
        })),
      };
      await firstValueFrom(this.equipmentService.merge(mergeDto));
      this.isEditing.set(false);
      this.toastrService.success('Protokolltypen gespeichert', 'Erfolg');
      this.loadData(this.tgaTypeId);
    } catch {
      this.toastrService.danger('Protokolltypen konnten nicht gespeichert werden.', 'Fehler');
    } finally {
      this.saving$.next(false);
    }
  }

  hasPendingChanges(): boolean {
    return this.isEditing();
  }

  get availableTypes(): ProtocolTypeItemDto[] {
    return this.allProtocolTypes$.value;
  }
}
